import React from 'react';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';

import type { ProcessingTask } from '@stores/taskProcessing.types';
import { Card } from '@components/ui/Card';
import { Icon } from '@components/ui/Icon';
import { Button } from '@components/ui/Button';
import { formatRelativeTime, formatTime } from '@utils/formatters';

export interface TaskStatusCardProps {
  task: ProcessingTask;
  className?: string;
}

interface TaskStatusToken {
  icon: 'time' | 'refresh' | 'warning' | 'success' | 'error' | 'info';
  label: string;
  description: string;
  tone: 'info' | 'brand' | 'warning' | 'success' | 'danger' | 'neutral';
  action?: {
    getLabel: (task: ProcessingTask) => string;
    getHref: (task: ProcessingTask) => string | null;
  };
}

const TASK_STATUS_TOKENS: Record<
  ProcessingTask['normalizedStatus'],
  TaskStatusToken
> = {
  queued: {
    icon: 'time',
    label: 'En espera',
    description: 'La tarea está en cola y comenzará pronto.',
    tone: 'info',
  },
  processing: {
    icon: 'refresh',
    label: 'Procesando',
    description: 'Estamos procesando tu documento.',
    tone: 'brand',
  },
  retrying: {
    icon: 'warning',
    label: 'Reintentando',
    description: 'Se detectó un error, reintentando automáticamente.',
    tone: 'warning',
  },
  success: {
    icon: 'success',
    label: 'Completado',
    description: 'Tarea completada correctamente.',
    tone: 'success',
    action: {
      getLabel: (task) =>
        task.type === 'sales'
          ? 'Ver resultados de ventas'
          : 'Ver resultados de factura',
      getHref: (task) => {
        if (!task.resultId) {
          return null;
        }

        const isInvoiceTask = task.subtype
          .split('.')
          .some((segment) => segment.includes('invoice'));
        const isSalesTask = task.subtype
          .split('.')
          .some((segment) => segment.includes('sales'));

        if (isInvoiceTask) {
          const url = new URL('/invoice-results', window.location.origin);
          url.searchParams.set('id', String(task.resultId));
          url.searchParams.set('type', task.type);
          return `${url.pathname}${url.search}`;
        }

        if (isSalesTask) {
          return `/sales-results/${task.resultId}`;
        }

        return `/transactions/${task.resultId}`;
      },
    },
  },
  failure: {
    icon: 'error',
    label: 'Error',
    description: 'No pudimos completar la tarea.',
    tone: 'danger',
  },
  canceled: {
    icon: 'info',
    label: 'Cancelado',
    description: 'La tarea se canceló antes de finalizar.',
    tone: 'neutral',
  },
};

const toneVariants = {
  info: css`
    --badge-bg: var(--info-100);
    --badge-icon: var(--info-600);
  `,
  brand: css`
    --badge-bg: var(--brand-50);
    --badge-icon: var(--brand-600);
  `,
  warning: css`
    --badge-bg: var(--warning-100);
    --badge-icon: var(--warning-600);
  `,
  success: css`
    --badge-bg: var(--success-100);
    --badge-icon: var(--success-600);
  `,
  danger: css`
    --badge-bg: var(--danger-100);
    --badge-icon: var(--danger-600);
  `,
  neutral: css`
    --badge-bg: var(--neutral-100);
    --badge-icon: var(--neutral-600);
  `,
} as const;

const StyledCard = styled(Card)<{ $tone: TaskStatusToken['tone'] }>`
  display: flex;
  gap: var(--space-lg);
  align-items: flex-start;
  border-color: var(--badge-bg);
  background: var(--surface);

  ${({ $tone }) => toneVariants[$tone]}
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-full);
  background-color: var(--badge-bg);
  color: var(--badge-icon);
  flex-shrink: 0;

  svg {
    color: var(--badge-icon);
  }
`;

const TaskContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  width: 100%;
`;

const TaskTitle = styled.span`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
`;

const TaskStatus = styled.span`
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--badge-icon);
`;

const TaskDescription = styled.span`
  font-size: var(--font-size-xs);
  color: var(--text-muted);
`;

const TaskMetadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-top: var(--space-1);
`;

const TaskMetadataItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: var(--font-size-xs);
  color: var(--text-secondary);

  svg {
    color: var(--text-muted);
    flex-shrink: 0;
  }
`;

const FileName = styled.span`
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TaskError = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--danger-50);
  color: var(--danger-600);
  font-size: var(--font-size-xs);
  border: 1px solid var(--danger-100);
`;

const TaskActions = styled.div`
  margin-top: var(--space-sm);
  display: inline-flex;
  align-items: center;
`;

export function TaskStatusCard({ task, className }: TaskStatusCardProps) {
  const navigate = useNavigate();
  const statusToken =
    TASK_STATUS_TOKENS[task.normalizedStatus] ?? TASK_STATUS_TOKENS.processing;

  if (!statusToken) {
    throw new Error(`Unsupported task status: ${task.normalizedStatus}`);
  }

  const title = task.subtype !== 'unknown' ? task.subtype : 'Tarea en curso';
  const sanitizedId = React.useMemo(
    () => task.id.replace(/[^a-zA-Z0-9_-]/g, '-'),
    [task.id],
  );
  const descriptionId = `task-description-${sanitizedId}`;
  const rawError = typeof task.error === 'string' ? task.error.trim() : '';
  const errorMessage =
    task.normalizedStatus === 'failure' && rawError.length > 0
      ? rawError
      : null;
  const errorId = errorMessage ? `task-error-${sanitizedId}` : undefined;
  const describedBy = React.useMemo(
    () =>
      [descriptionId, errorId]
        .filter((value): value is string => Boolean(value))
        .join(' '),
    [descriptionId, errorId],
  );
  const actionHref = statusToken.action?.getHref(task) ?? null;

  const handleViewResults = React.useCallback(() => {
    if (actionHref) {
      navigate(actionHref);
    }
  }, [actionHref, navigate]);

  // Extract metadata
  const filename = task.details?.filename as string | undefined;
  const startedAt = task.timestamps.startedAt;
  const completedAt = task.timestamps.completedAt;

  // Format timestamps
  const timeDisplay = React.useMemo(() => {
    if (completedAt) {
      // For completed tasks, show completion time
      return `Completado ${formatRelativeTime(completedAt)} (${formatTime(completedAt)})`;
    }
    if (startedAt) {
      // For ongoing tasks, show when started
      return `Iniciado ${formatRelativeTime(startedAt)}`;
    }
    return null;
  }, [startedAt, completedAt]);

  return (
    <StyledCard
      className={className}
      role="status"
      aria-live={task.normalizedStatus === 'failure' ? 'assertive' : 'polite'}
      aria-atomic="true"
      aria-describedby={describedBy || undefined}
      $tone={statusToken.tone}
    >
      <StatusBadge aria-hidden="true">
        <Icon name={statusToken.icon} size="sm" aria-hidden />
      </StatusBadge>
      <TaskContent>
        <TaskTitle>{title}</TaskTitle>
        <TaskStatus>{statusToken.label}</TaskStatus>
        <TaskDescription id={descriptionId}>
          {statusToken.description}
        </TaskDescription>

        {/* Task metadata: filename and timestamps */}
        {(filename || timeDisplay) && (
          <TaskMetadata>
            {filename && (
              <TaskMetadataItem>
                <Icon name="file" size="xs" aria-hidden />
                <FileName title={filename}>{filename}</FileName>
              </TaskMetadataItem>
            )}
            {timeDisplay && (
              <TaskMetadataItem>
                <Icon name="time" size="xs" aria-hidden />
                <span>{timeDisplay}</span>
              </TaskMetadataItem>
            )}
          </TaskMetadata>
        )}

        {errorMessage ? (
          <TaskError id={errorId} role="alert">
            {errorMessage}
          </TaskError>
        ) : null}
        {statusToken.action && actionHref ? (
          <TaskActions>
            <Button onClick={handleViewResults} variant="secondary" size="sm">
              {statusToken.action.getLabel(task)}
            </Button>
          </TaskActions>
        ) : null}
      </TaskContent>
    </StyledCard>
  );
}

TaskStatusCard.displayName = 'TaskStatusCard';
